
import React, { useState, useEffect } from 'react';
import { startOfMonth, endOfMonth, eachDayOfInterval, format, isSameDay, addMonths, subMonths } from 'date-fns';

type DailyCount = {
  date: string;
  count: number;
};

type CalendarData = {
  [date: string]: {
    count: number;
  };
};

type ContributionCalendarProps = {
  data: DailyCount[];
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
};

const ContributionCalendar: React.FC<ContributionCalendarProps> = ({ 
  data,
  currentMonth,
  onMonthChange
}) => {
  const [calendarData, setCalendarData] = useState<CalendarData>({});
  
  // Organize data by date
  useEffect(() => {
    const organizedData: CalendarData = {};
    
    data.forEach(item => {
      // 将日期格式转换为 yyyy-MM-dd
      const dateObj = new Date(item.date);
      const dateStr = format(dateObj, 'yyyy-MM-dd');
      
      organizedData[dateStr] = {
        count: item.count
      };
    });
    
    setCalendarData(organizedData);
  }, [data]);
  
  // Navigate to previous month
  const goToPreviousMonth = () => {
    onMonthChange(subMonths(currentMonth, 1));
  };
  
  // Navigate to next month
  const goToNextMonth = () => {
    onMonthChange(addMonths(currentMonth, 1));
  };
  
  // Get days of current month
  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });
  
  // Get color intensity based on count
  const getColorIntensity = (count: number) => {
    if (count === 0) return 'bg-gray-100';
    if (count <= 2) return 'bg-green-200';
    if (count <= 5) return 'bg-green-300';
    if (count <= 8) return 'bg-green-400';
    return 'bg-green-500';
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">番茄钟贡献日历</h3>
        <div className="flex items-center space-x-2">
          <button 
            onClick={goToPreviousMonth}
            className="p-1 rounded hover:bg-gray-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          <span className="text-sm font-medium">
            {format(currentMonth, 'yyyy年 M月')}
          </span>
          <button 
            onClick={goToNextMonth}
            className="p-1 rounded hover:bg-gray-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {['日', '一', '二', '三', '四', '五', '六'].map((day, index) => (
          <div key={`header-${index}`} className="text-center text-xs font-medium text-gray-500 py-1">
            {day}
          </div>
        ))}
        
        {/* Empty cells for days before first day of month */}
        {Array.from({ length: startOfMonth(currentMonth).getDay() }).map((_, index) => (
          <div key={`empty-start-${index}`} className="h-8"></div>
        ))}
        
        {/* Calendar days */}
        {daysInMonth.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const dayData = calendarData[dateStr] || { count: 0 };
          
          return (
            <div 
              key={dateStr} 
              className="relative"
            >
              <div 
                className={`
                  h-8 text-center rounded-md flex items-center justify-center
                  ${getColorIntensity(dayData.count)}
                  hover:ring-2 hover:ring-blue-400 transition-all cursor-pointer
                `}
                title={`${format(day, 'yyyy-MM-dd')} - ${dayData.count} 个番茄钟`}
              >
                <span className="text-xs">{format(day, 'd')}</span>
                {dayData.count > 0 && (
                  <span className="absolute bottom-0.5 right-1 text-[9px] font-bold">
                    {dayData.count}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        
        {/* Empty cells for days after last day of month */}
        {Array.from({ length: 6 - endOfMonth(currentMonth).getDay() }).map((_, index) => (
          <div key={`empty-end-${index}`} className="h-8"></div>
        ))}
      </div>
      
      <div className="mt-4 flex items-center justify-end text-xs text-gray-500">
        <div className="flex items-center space-x-1">
          <span>少</span>
          <div className="w-3 h-3 bg-gray-100 rounded"></div>
          <div className="w-3 h-3 bg-green-200 rounded"></div>
          <div className="w-3 h-3 bg-green-300 rounded"></div>
          <div className="w-3 h-3 bg-green-400 rounded"></div>
          <div className="w-3 h-3 bg-green-500 rounded"></div>
          <span>多</span>
        </div>
      </div>
    </div>
  );
};

export default ContributionCalendar; 